import { gql } from '@apollo/client';

export const APPLICATIONS_DASHBOARD_QUERY = gql`
  query ApplicationsDashboardQuery($listingAlias: ID!) {
    rentalListing(id: $listingAlias) {
      address {
        street
        city
        state
        zip
      }
      landlordRentalApplications {
        id
        overallStatus
        applicants {
          id
          userId
          answers {
            firstName
            middleName
            lastName
          }
          submittedDateTime
        }
        withdrawnDateTime
        declinedDateTime
        invitedToMoveInDateTime
      }
      invitationsToApply {
        id
        email
        firstName
        lastName
        invitedDateTime
      }
    }
  }
`;

export const PRINT_APPLICATION_QUERY = gql`
  query PrintApplication($listingAlias: String!, $applicationId: String!) {
    user {
      termSet(id: "landlordScreening") {
        status
      }
      idVerification {
        status
      }
      features {
        canAccessBusinessVerificationLite
      }
      userManagementCompany {
        id
        employees {
          id
          role
          contactInfo {
            firstName
            lastName
            email
          }
          idVerification {
            status
          }
          termSet {
            status
          }
        }
      }
    }
    property(id: $listingAlias) {
      id
      isBusinessVerificationLiteEligible
      application(id: $applicationId) {
        id
        icraaPageConfig {
          hasBeenAcceptedFor
        }
        applicants {
          userId
          status
        }
      }
    }
  }
`;

export const APPLICANT_FRAGMENT = gql`
  fragment ApplicantFragment on Applicant {
    id
    userId
    zgAuthZone
    answers {
      firstName
      middleName
      lastName
      email
      phone
      occupants
      isSmoker
      currentIncomes {
        id
        isVerified
        note
        amount
        title
        type
        documents {
          id
          previewUrl
        }
        ... on EmploymentIncome {
          employer {
            organization
            name
            email
            phone
          }
          startDate
        }
      }
      reasonForMoving
      moveInDate
      isMoveInDateFlexible
      pets {
        smallDogs
        mediumDogs
        largeDogs
        cats
        other
        description
      }
      pastJobs {
        title
        employer {
          organization
          name
          email
          phone
        }
        startDate
        endDate
      }
      currentResidence {
        type
        address {
          street
          city
          state
          zip
        }
        startDate
        monthlyPayment
        ... on OtherCurrentResidence {
          description
        }
        ... on RentalCurrentResidence {
          manager {
            name
            email
            phone
          }
        }
      }
      pastResidences {
        id
        type
        address {
          street
          city
          state
          zip
        }
        startDate
        endDate
        ... on OtherPastResidence {
          description
        }
        ... on RentalPastResidence {
          manager {
            name
            email
            phone
          }
        }
      }
    }
    userStatuses {
      type
      status
      expirationDateTime
      requestedDateTime
    }
    submittedDateTime
    isPrimaryApplicant
    status
    coapplicantInvitation {
      firstName
      lastName
      email
    }
  }
`;

export const SINGLE_PDF_QUERY = gql`
  query SinglePdf($listingAlias: ID!, $applicationId: ID!, $applicantId: ID!, $zgAuthZone: ZgAuthZone!) {
    rentalListing(id: $listingAlias) {
      landlordRentalApplication(id: $applicationId) {
        applicant(userId: $applicantId, zgAuthZone: $zgAuthZone) {
          ...ApplicantFragment
        }
      }
    }
  }
  ${APPLICANT_FRAGMENT}
`;

export const COMBINED_PDF_QUERY = gql`
  query CombinedPdf($listingAlias: ID!, $applicationId: ID!) {
    rentalListing(id: $listingAlias) {
      address {
        street
        city
        state
        zip
      }
      monthlyPayment
      landlordRentalApplication(id: $applicationId) {
        applicants {
          ...ApplicantFragment
        }
        invitations {
          firstName
          lastName
          email
        }
      }
    }
  }
  ${APPLICANT_FRAGMENT}
`;

export const DESCRIPTION_QUERY = gql`
  query HasIncomeDocuments(
    $listingAlias: ID!
    $applicationId: ID!
    $applicantId: ID!
    $zgAuthZone: ZgAuthZone!
    $needsToIdVerify: Boolean!
  ) {
    rentalListing(id: $listingAlias) {
      id
      landlordRentalApplication(id: $applicationId) {
        id
        applicant(userId: $applicantId, zgAuthZone: $zgAuthZone) {
          id
          userId
          zgAuthZone
          status
          answers {
            id
            currentIncomes {
              id
              amount
              documents {
                id
                expirationDateTime @skip(if: $needsToIdVerify)
              }
            }
          }
        }
      }
    }
  }
`;

export const LISTING_APPLICATIONS_QUERY = gql`
  query ListingApplications($listingAlias: ID!, $isActive: Boolean, $needsToIdVerify: Boolean!) {
    rentalListing(id: $listingAlias) {
      id
      landlordRentalApplications(isActive: $isActive) {
        id
        overallStatus
        isViewed
        withdrawnDateTime
        declinedDateTime
        mergedDateTime
        conditionallyApprovedDateTime
        invitedToMoveInDateTime
        applicants {
          id
          userId
          zgAuthZone
          status
          submittedDateTime
          landlordInvitation {
            id
            firstName
            lastName
            email
            invitedDateTime
          }
          coapplicantInvitation {
            id
            firstName
            lastName
            email
            invitedDateTime
          }
          answers {
            firstName
            middleName
            lastName
            email
            currentIncomes {
              id
              areDocumentsExpired @skip(if: $needsToIdVerify)
              documents {
                id
              }
            }
          }
        }
        invitations {
          id
          email
          firstName
          lastName
          invitedDateTime
        }
      }
      invitationsToApply {
        id
        email
        firstName
        lastName
        invitedDateTime
      }
    }
  }
`;

export const MARK_APPLICATIONS_AS_VIEWED_MUTATION = gql`
  mutation MARK_APPLICATIONS_AS_VIEWED_MUTATION($listingAlias: ID!) {
    markApplicationsAsViewed(listingAlias: $listingAlias) {
      id
      isViewed
    }
  }
`;

export const APPLICATION_SUMMARY_QUERY = gql`
  query ApplicationSummary($listingAlias: ID!, $applicationId: ID!) {
    rentalListing(id: $listingAlias) {
      id
      landlordRentalApplication(id: $applicationId) {
        id
        overallStatus
        conditionallyApprovedDateTime
        invitedToMoveInDateTime
        declinedDateTime
        declinedReasons {
          message
        }
      }
    }
  }
`;
